/**
 * The URL a share actually puts on somebody's clipboard.
 *
 * Sharing is the growth loop and it was invisible in the funnel. Two separate
 * reasons, both fixed here.
 *
 * **The link carried no attribution.** The share sheet handed out
 * `window.location.href` verbatim, so a link posted into WhatsApp arrived at
 * the event page indistinguishable from somebody typing the address in. Entry
 * attribution then fell back to `document.referrer`, which in-app browsers
 * strip — so every organic share was filed under "Direct link" and "Where they
 * came from" could never show a share at all.
 *
 * **It also carried the app's own junk.** `?from=%2Fhiives` is the back-button
 * breadcrumb a `BackAwareLink` writes; `?story=` and `?instants=` open
 * overlays. Those are facts about the sharer's session, not about the thing
 * being shared, and they were being posted to Instagram.
 *
 * The parameters are deliberately readable rather than opaque: a person looking
 * at `?ref=share&ref_from=event_page` can tell what it does, which matters for
 * a link that gets pasted in public.
 */

/** Marks a link as having come out of the share sheet. */
export const SHARE_REF_PARAM = "ref";
export const SHARE_REF_VALUE = "share";
/** Which surface the share sheet was opened from. */
export const SHARE_SURFACE_PARAM = "ref_from";

/**
 * Query parameters that describe the sharer's session rather than the thing
 * being shared. Stripped on the way out.
 */
export const SESSION_ONLY_SHARE_PARAMS = [
  // Back-button breadcrumb written by BackAwareLink.
  "from",
  // Root overlay openers.
  "story",
  "instants",
  // Auth/checkout handoff.
  "flow",
  "callbackURL",
  "redirect",
  "code",
];

export type ShareSurface =
  | "event_page"
  | "hiive_page"
  | "venue_page"
  | "experience_page"
  | "profile_page"
  | "post"
  | "shop_item"
  | "event_menu"
  | "story"
  | "chat"
  | "other";

/**
 * Clean the link and stamp it as a share.
 *
 * Returns the input unchanged if it cannot be parsed — a share that silently
 * fails is worse than one with an untidy URL.
 */
export function buildShareUrl(
  rawUrl: string,
  options: { surface?: ShareSurface } = {},
): string {
  try {
    const url = new URL(rawUrl);
    for (const param of SESSION_ONLY_SHARE_PARAMS)
      url.searchParams.delete(param);
    url.searchParams.set(SHARE_REF_PARAM, SHARE_REF_VALUE);
    if (options.surface) {
      url.searchParams.set(SHARE_SURFACE_PARAM, options.surface);
    }
    return url.toString();
  } catch {
    return rawUrl;
  }
}

/** Was this page opened from a shared link? Reads a query string. */
export function isSharedLinkArrival(search: string): boolean {
  try {
    return new URLSearchParams(search).get(SHARE_REF_PARAM) === SHARE_REF_VALUE;
  } catch {
    return false;
  }
}

/** The surface a shared link was created from, when it says. */
export function sharedLinkSurface(search: string): string | null {
  try {
    const value = new URLSearchParams(search).get(SHARE_SURFACE_PARAM);
    return value?.trim() || null;
  } catch {
    return null;
  }
}
